import type { MovementConfig } from '../config/movements';
import type { TransportDetails } from '../types/wizard';
import { isPlaceholderDriverMobile } from './transportFieldUtils';

export type UdfFieldError = { field: keyof TransportDetails; message: string };

/** Per-field validation copy from `t.UdfValidation` (translation API + fallbacks). */
/** Map `t.UdfValidation` bundle keys to validation messages. */
export function pickUdfValidationMessages(
  udf: Record<string, string>
): UdfValidationMessages {
  return {
    vehicleNoRequired: udf.vehicleNoRequired,
    transporterNameRequired: udf.transporterNameRequired,
    driverNameRequired: udf.driverNameRequired,
    driverMobileRequired: udf.driverMobileRequired,
    driverMobileTenDigits: udf.driverMobileTenDigits,
    lrRequired: udf.lrRequired,
    freightRequired: udf.freightRequired,
    transportationByRequired: udf.transportationByRequired,
    vehicleTypeRequired: udf.vehicleTypeRequired,
    transportModeRequired: udf.transportModeRequired,
    distanceRequired: udf.distanceRequired,
    reasonRequired: udf.reasonRequired,
    containerSealRequired: udf.containerSealRequired,
    ewayRequired: udf.ewayRequired,
    podRequired: udf.podRequired,
  };
}

export type UdfValidationMessages = {
  vehicleNoRequired: string;
  transporterNameRequired: string;
  driverNameRequired: string;
  driverMobileRequired: string;
  driverMobileTenDigits: string;
  lrRequired: string;
  freightRequired: string;
  transportationByRequired: string;
  vehicleTypeRequired: string;
  transportModeRequired: string;
  distanceRequired: string;
  reasonRequired: string;
  containerSealRequired: string;
  ewayRequired: string;
  podRequired: string;
};

function missing(v: string | undefined): boolean {
  return !String(v ?? '').trim();
}

/** Vehicle, driver name, and mobile — required on every movement before scan/submit. */
export function validateTransportCoreFields(
  transport: TransportDetails,
  msg: UdfValidationMessages
): UdfFieldError[] {
  const errors: UdfFieldError[] = [];

  if (missing(transport.VEHICLE_NO)) {
    errors.push({ field: 'VEHICLE_NO', message: msg.vehicleNoRequired });
  }
  if (missing(transport.DRIVER_NAME)) {
    errors.push({ field: 'DRIVER_NAME', message: msg.driverNameRequired });
  }
  if (isPlaceholderDriverMobile(transport.DRIVER_MOB_NO)) {
    errors.push({ field: 'DRIVER_MOB_NO', message: msg.driverMobileRequired });
  } else if (String(transport.DRIVER_MOB_NO).trim().length !== 10) {
    errors.push({ field: 'DRIVER_MOB_NO', message: msg.driverMobileTenDigits });
  }

  return errors;
}

/** Full UDF set for standard and relocation flows. */
export function validateTransportUdf(
  transport: TransportDetails,
  msg: UdfValidationMessages
): UdfFieldError[] {
  const errors: UdfFieldError[] = [...validateTransportCoreFields(transport, msg)];

  if (missing(transport.TRANSPORTER_NAME)) {
    errors.push({ field: 'TRANSPORTER_NAME', message: msg.transporterNameRequired });
  }
  if (missing(transport.LR_NO)) errors.push({ field: 'LR_NO', message: msg.lrRequired });
  if (missing(transport.freight)) errors.push({ field: 'freight', message: msg.freightRequired });
  if (missing(transport.TRANPORTATION_BY)) {
    errors.push({ field: 'TRANPORTATION_BY', message: msg.transportationByRequired });
  }
  if (missing(transport.VEHICLE_TYPE)) {
    errors.push({ field: 'VEHICLE_TYPE', message: msg.vehicleTypeRequired });
  }
  if (missing(transport.TRANSPORT_MODE)) {
    errors.push({ field: 'TRANSPORT_MODE', message: msg.transportModeRequired });
  }
  if (missing(transport.reason)) errors.push({ field: 'reason', message: msg.reasonRequired });
  if (missing(transport.containerSeal)) {
    errors.push({ field: 'containerSeal', message: msg.containerSealRequired });
  }
  if (missing(transport.eway)) errors.push({ field: 'eway', message: msg.ewayRequired });
  if (missing(transport.pod)) errors.push({ field: 'pod', message: msg.podRequired });

  return errors;
}

export function validateMovementCapture(
  movement: MovementConfig,
  transport: TransportDetails,
  msg: UdfValidationMessages
): UdfFieldError[] {
  if (movement.wizardKind === 'standard' || movement.wizardKind === 'relocation') {
    return validateTransportUdf(transport, msg);
  }
  return validateTransportCoreFields(transport, msg);
}
